import { Slot } from 'radix-ui'
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cx } from './cx.ts'
import { Keys } from './Kbd.tsx'
import { Spinner } from './Spinner.tsx'
import { Tooltip } from './Tooltip.tsx'

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** primary = safety orange with the 45° facet (one per view); secondary; quiet; danger (destructive confirm). */
  variant?: ButtonVariant
  size?: ButtonSize
  /** Leading icon. */
  icon?: ReactNode
  trailingIcon?: ReactNode
  /** Shortcut shown inside the button (e.g. "a" for approve). */
  kbd?: string
  loading?: boolean
  block?: boolean
  /** Text colour for destructive quiet/secondary buttons. */
  tone?: 'danger'
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, trailingIcon, kbd, loading, block, tone, asChild, className, children, disabled, type, ...rest },
  ref,
) {
  const Comp = asChild ? Slot.Root : 'button'
  const content = asChild ? (
    children
  ) : (
    <>
      {loading ? (
        <span className="rt-btn__spin">
          <Spinner delay={0} />
        </span>
      ) : (
        icon
      )}
      {children !== undefined && children !== null ? <span>{children}</span> : null}
      {trailingIcon}
      {kbd ? <Keys keys={kbd} className="rt-btn__kbd" /> : null}
    </>
  )
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? 'button')}
      className={cx('rt-btn', className)}
      data-variant={variant}
      data-size={size}
      data-block={block ? '' : undefined}
      data-tone={tone}
      disabled={asChild ? undefined : disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </Comp>
  )
})

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  /** Accessible name; also shown as the tooltip. */
  label: string
  icon: ReactNode
  size?: 'sm' | 'md' | 'lg'
  variant?: 'quiet' | 'secondary'
  keys?: string
  tooltip?: boolean
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left'
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, size = 'md', variant = 'quiet', keys, tooltip = true, tooltipSide, className, type, ...rest },
  ref,
) {
  const btn = (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-label={label}
      className={cx('rt-iconbtn', className)}
      data-size={size}
      data-variant={variant}
      {...rest}
    >
      {icon}
    </button>
  )
  return tooltip ? (
    <Tooltip content={label} keys={keys} side={tooltipSide}>
      {btn}
    </Tooltip>
  ) : (
    btn
  )
})
